<template>
    <div class="flex flex-col gap-4">
        <h3 class="text-sm font-medium text-muval-gray-1">{{ config.heading }}</h3>

        <template v-if="!disabled">
            <div
                class="flex flex-wrap items-start gap-3"
                data-resource-toolbar
            >
                <MuvalSearch
                    v-model="search"
                    class="!w-full min-w-56 md:!w-auto md:flex-1"
                    :placeholder="config.searchPlaceholder"
                    highlightOnValue
                    on-input
                />

                <!--
                    The vehicle tab carries the operations assign-transport modal's own
                    filter, restricted to the three questions this endpoint can answer:
                    transport type in the bar, associated depot and borrowed/lease
                    provider behind "Additional filters". The run-relative ones it also
                    offers (scheduling conflicts, last usage) are absent rather than
                    inert - a diary entry has no run to be relative to.

                    Search and filters share the row from `md` upward. Below that the
                    filter takes a full basis and wraps without squeezing either control.
                -->
                <AssignResourceFilter
                    v-if="kind === 'transport'"
                    class="min-w-0 basis-full md:!w-auto md:flex-1 md:basis-0"
                    context="transport"
                    :responsive-toolbar="true"
                    hide-capacity
                    :available-filters="TRANSPORT_FILTER_KEYS"
                    :filter-options="filterOptions"
                    :default-filters="{}"
                    :is-loading="isLoading"
                    :refetch="refetch"
                    :active-filters="activeFilters"
                    :draft-filters="draftFilters"
                    :active-filter-count="activeFilterCount"
                    @update:active-filters="applyFilters"
                    @update:draft-filters="(value) => (draftFilters = value)"
                    @clear-all-filters="clearFilters"
                />
            </div>

            <!--
                Available and Issues are two views of one list rather than two
                queries: the counts have to agree with each other, and a resource
                moves between them the moment the entry's dates change.
            -->
            <div class="grid grid-cols-2 gap-1 rounded-muval-2 bg-muval-gray-6 p-1">
                <button
                    v-for="segment in segments"
                    :key="segment.key"
                    type="button"
                    class="rounded-muval-1 px-3 py-2 text-sm font-medium transition"
                    :class="
                        activeSegment === segment.key ?
                            'bg-white text-muval-gray-1 shadow-sm'
                        :   'text-muval-gray-2 hover:text-muval-gray-1'
                    "
                    :data-segment="segment.key"
                    @click="activeSegment = segment.key"
                >
                    {{ segment.label }} ({{ segment.count }})
                </button>
            </div>
        </template>

        <!--
            Availability needs a window to be about. Until the entry has dates the
            roster is still offered - assigning first and dating after is a
            legitimate order to work in - but nothing is claimed about who is free.
        -->
        <p
            v-if="!hasWindow && !disabled"
            class="rounded-muval-2 bg-muval-gray-6 px-3 py-2 text-xs text-muval-gray-2"
        >
            Set the entry's dates on the General tab to see who and what is free.
        </p>

        <div
            v-if="selected.length > 0"
            class="flex flex-col gap-2"
        >
            <span class="text-xs font-medium text-muval-gray-2">{{ config.selectedLabel }}</span>
            <div class="flex flex-wrap gap-2">
                <div
                    v-for="item in selected"
                    :key="item.id"
                    class="flex items-center gap-2 rounded-muval-2 py-1 pl-1 pr-2 text-sm"
                    :class="item.is_available === false ? 'bg-muval-red-2' : 'bg-muval-gray-6'"
                    :data-selected-chip="item.id"
                >
                    <span
                        class="grid size-6 shrink-0 place-items-center rounded-full bg-white text-[10px] font-medium text-muval-gray-2"
                        v-text="initials(item.name)"
                    ></span>
                    <span
                        class="truncate"
                        v-text="item.name"
                    ></span>
                    <button
                        v-if="!disabled"
                        type="button"
                        class="shrink-0 text-muval-gray-3 hover:text-muval-gray-1"
                        :aria-label="`Remove ${item.name}`"
                        @click="toggle(item.id)"
                    >
                        <SvgIcon
                            name="system-close"
                            class="size-3"
                        />
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="!disabled"
            class="overflow-hidden rounded-muval-2 border border-muval-gray-5"
        >
            <div
                v-if="isLoading"
                class="flex items-center justify-center gap-2 py-10 text-sm text-muval-gray-2"
            >
                <div class="size-4 animate-spin rounded-full border-2 border-muval-gray-4 border-t-brand"></div>
                Loading…
            </div>

            <!--
                An availability request that fails must never render as an empty
                list: "nobody is free" and "we could not ask" look identical on
                screen but mean opposite things, and the first one silently invites
                the operator to assign nothing. Its own state, with a retry.
            -->
            <div
                v-else-if="isError"
                class="flex flex-col items-center gap-2 py-10 text-center text-sm"
                data-availability-error
            >
                <p class="text-muval-error">Couldn't load availability.</p>
                <button
                    type="button"
                    class="text-sm font-medium text-brand hover:underline"
                    @click="refetch()"
                >
                    Try again
                </button>
            </div>

            <p
                v-else-if="visible.length === 0"
                class="py-10 text-center text-sm text-muval-gray-2"
            >
                {{ activeSegment === 'issues' ? config.noIssuesLabel : config.emptyLabel }}
            </p>

            <!--
                A row with a conflict stays selectable, unlike the operations assign
                modals. Booking off a driver who is rostered on a job is the
                commonest reason this modal is open - they called in sick - so the
                clash is shown and the choice is left with the operator.
            -->
            <div
                v-for="resource in visible"
                v-else
                :key="resource.id"
                class="flex cursor-pointer items-center gap-3 border-b border-muval-gray-5 px-4 py-3 transition last:border-b-0"
                :class="isSelected(resource.id) ? 'bg-muval-gray-6' : 'bg-white hover:bg-muval-gray-6'"
                :data-resource-row="resource.id"
                @click="toggle(resource.id)"
            >
                <span
                    class="grid size-8 shrink-0 place-items-center rounded-full bg-muval-gray-5 text-xs font-medium text-muval-gray-2"
                    v-text="initials(resource.name)"
                ></span>

                <div class="flex min-w-0 grow flex-col">
                    <span
                        class="truncate text-sm text-muval-gray-1"
                        v-text="resource.name"
                    ></span>
                    <span
                        v-if="resource.subtitle"
                        class="truncate text-xs text-muval-gray-3"
                        v-text="resource.subtitle"
                    ></span>
                </div>

                <!--
                        The badge is the whole affordance: a dot, the word, and an
                        info glyph. It opens a side panel rather than expanding in
                        place, because a clash is only actionable once you can see
                        *which* job holds the resource, where, and when - more than
                        a row's height can carry.
                    -->
                <button
                    v-if="conflictReasons(resource).length > 0"
                    type="button"
                    class="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium text-error hover:underline"
                    :aria-label="`View conflicts for ${resource.name}`"
                    :data-conflict-trigger="resource.id"
                    @click.stop="openConflicts(resource)"
                >
                    <span class="size-1.5 shrink-0 rounded-full bg-error"></span>
                    Conflict
                    <SvgIcon
                        name="system-information"
                        class="size-3.5 shrink-0"
                    />
                </button>

                <SvgIcon
                    v-if="isSelected(resource.id)"
                    name="system-check"
                    class="size-5 shrink-0 text-brand"
                />
                <span
                    v-else
                    class="size-5 shrink-0"
                ></span>
            </div>
        </div>

        <CompanyDiaryResourceConflictModal
            v-if="conflictResource"
            v-model="conflictOpen"
            :resourceName="conflictResource.name"
            :reasons="conflictReasons(conflictResource)"
        />

        <p
            v-if="disabled && selected.length === 0"
            class="text-sm text-muval-gray-2"
        >
            {{ config.emptyLabel }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import companyDiaryApi, {
    type CompanyDiaryAssignableResource,
    type CompanyDiaryAvailabilityReason,
    type CompanyDiaryResourceKindKey,
    type CompanyDiaryResourceSummary,
} from '@/api/companyDiary';
import CompanyDiaryResourceConflictModal from './CompanyDiaryResourceConflictModal.vue';
import AssignResourceFilter from '@components/diary/AssignResourceFilter.vue';
import MuvalSearch from '@components/inputs/MuvalSearch.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import { useAffiliates } from '@composables/useAffiliates';
import { useDepotFilterOptions, useTransportTypeFilterOptions } from '@composables/useResourceFilterOptions';
import { useUserStore } from '@store/user';

/**
 * One resource tab on the company diary entry modal - staff, vehicles, containers
 * or trailers - as a searchable list carrying availability.
 *
 * The operations diary answers "who is free" per `ScheduleServiceEvent`; an entry
 * being created has no event yet, so this reads the diary's own window-rooted
 * endpoint instead. Containers and trailers are two tabs over one `vessel_ids`
 * payload: the split is presentational, and the modal merges them on save.
 *
 * **A conflict never blocks a choice here.** The operations modals grey out an
 * unavailable resource; this one files it under Issues and leaves the row
 * selectable, because marking someone unavailable *while they are rostered on* is
 * the case the diary exists to serve.
 */

const props = withDefaults(
    defineProps<{
        kind: CompanyDiaryResourceKindKey;
        /** Selected resource ids. */
        modelValue: number[];
        startDatetime?: string | null;
        endDatetime?: string | null;
        /**
         * Whole days rather than times, matching how the entry itself is authored.
         * Passed through so the API expands the sentinel range through its one
         * owner - composing it here would let the picker and the saved entry
         * disagree about what "all day" spans, and the disagreement would surface
         * as a zero-length window in which everything looks free.
         */
        allDay?: boolean;
        /** The entry being edited, excluded from its own conflict list. */
        excludeEntryId?: number | null;
        /**
         * What the entry already has assigned. Labels the selection chips before
         * the list has loaded, and keeps a selected resource named when a search
         * has filtered it out of view.
         */
        assigned?: CompanyDiaryResourceSummary[];
        disabled?: boolean;
    }>(),
    {
        startDatetime: null,
        endDatetime: null,
        allDay: false,
        excludeEntryId: null,
        assigned: () => [],
        disabled: false,
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: number[]): void;
}>();

const userStore = useUserStore();

const CONFIG: Record<
    CompanyDiaryResourceKindKey,
    { heading: string; selectedLabel: string; searchPlaceholder: string; emptyLabel: string; noIssuesLabel: string }
> = {
    staff: {
        heading: 'Assign staff',
        selectedLabel: 'Selected staff',
        searchPlaceholder: 'Search staff',
        emptyLabel: 'No staff found',
        noIssuesLabel: 'No staff have a clash with these dates',
    },
    transport: {
        heading: 'Assign vehicles',
        selectedLabel: 'Selected vehicles',
        searchPlaceholder: 'Search vehicles',
        emptyLabel: 'No vehicles found',
        noIssuesLabel: 'No vehicles have a clash with these dates',
    },
    container: {
        heading: 'Assign containers',
        selectedLabel: 'Selected containers',
        searchPlaceholder: 'Search containers',
        emptyLabel: 'No containers found',
        noIssuesLabel: 'No containers have a clash with these dates',
    },
    trailer: {
        heading: 'Assign trailers',
        selectedLabel: 'Selected trailers',
        searchPlaceholder: 'Search trailers',
        emptyLabel: 'No trailers found',
        noIssuesLabel: 'No trailers have a clash with these dates',
    },
};

const config = computed(() => CONFIG[props.kind]);

const selectedIds = computed(() => props.modelValue ?? []);
const isSelected = (id: number) => selectedIds.value.includes(id);

const search = ref('');
const debouncedSearch = ref('');
let searchTimer: ReturnType<typeof setTimeout> | null = null;

watch(search, (value) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
        debouncedSearch.value = value;
    }, 300);
});

const hasWindow = computed(() => Boolean(props.startDatetime && props.endDatetime));

/**
 * The vehicle filters, in `AssignResourceFilter`'s own key vocabulary. The order is
 * the shared component's, not this list's - the list only says which of its filters
 * this surface can honour.
 *
 * `showAssignedIds` is deliberately absent: it belongs to the operations modal's
 * pre-selection mode, which the diary's picker does not have.
 */
const TRANSPORT_FILTER_KEYS = ['types', 'depots', 'affiliates'] as const;

type TransportFilters = { types: string[]; depots: number[]; affiliates: number[]; showAssignedIds: boolean };

const emptyFilters = (): TransportFilters => ({ types: [], depots: [], affiliates: [], showAssignedIds: false });

/**
 * The always-visible filters, which auto-sync. `AssignResourceFilter` emits only a
 * draft for the controls in its bar - the Apply button belongs to the popover, and
 * the bar has none - so a key left off this list would move the draft and never the
 * query, leaving a control that visibly selects and silently filters nothing.
 *
 * Mirrors the operations assign-transport modal's `autoSyncFilters`, which gets the
 * same behaviour from `useFilterSystem`.
 */
const AUTO_SYNC_FILTER_KEYS = ['types'] as const;

/**
 * Draft is what the popover is editing; active is what the query is asking. The
 * split is the shared component's contract - the popover has an Apply button, and
 * without it every keystroke inside it would refetch the roster.
 */
const activeFilters = ref<TransportFilters>(emptyFilters());
const draftFilters = ref<TransportFilters>(emptyFilters());

const activeFilterCount = computed(
    () => TRANSPORT_FILTER_KEYS.filter((key) => (activeFilters.value[key] as unknown[]).length > 0).length,
);

let autoSyncTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced so a multi-select picked chip by chip asks once rather than per chip,
 * at the operations modal's own 300ms.
 *
 * The whole draft object is replaced on every change, including the popover's
 * filters, so the watcher compares the auto-synced keys by value and stays silent
 * when they are the same. Reassigning `activeFilters` regardless would give every
 * popover keystroke a fresh query key.
 */
watch(
    () => JSON.stringify(AUTO_SYNC_FILTER_KEYS.map((key) => draftFilters.value[key])),
    (value) => {
        if (autoSyncTimer) clearTimeout(autoSyncTimer);
        autoSyncTimer = setTimeout(() => {
            const synced = JSON.parse(value) as TransportFilters[(typeof AUTO_SYNC_FILTER_KEYS)[number]][];

            activeFilters.value = {
                ...activeFilters.value,
                ...Object.fromEntries(AUTO_SYNC_FILTER_KEYS.map((key, index) => [key, synced[index]])),
            };
        }, 300);
    },
);

/**
 * The popover's Apply and Clear carry the whole draft, so they settle the auto-synced
 * keys too - cancel any pending sync rather than let it land on top afterwards.
 */
const applyFilters = (value: TransportFilters) => {
    if (autoSyncTimer) clearTimeout(autoSyncTimer);
    activeFilters.value = { ...value };
    draftFilters.value = { ...value };
};

const clearFilters = () => {
    if (autoSyncTimer) clearTimeout(autoSyncTimer);
    activeFilters.value = emptyFilters();
    draftFilters.value = emptyFilters();
};

const { depotOptions } = useDepotFilterOptions();
const { typeOptions } = useTransportTypeFilterOptions();
const { affiliates } = useAffiliates();

const filterOptions = computed(() => ({
    typeOptions: typeOptions.value,
    depotOptions: depotOptions.value,
    affiliateOptions: affiliates.value ?? [],
}));

/**
 * The filters the request carries, in the endpoint's names. Empty lists are dropped
 * rather than sent: the API reads an empty array as "no filter", but leaving them in
 * the query key would make a cleared control look like a different question and
 * refetch for nothing.
 */
const filterParams = computed(() => {
    const params: Record<string, unknown> = {};

    if (activeFilters.value.types.length > 0) params.types = activeFilters.value.types;
    if (activeFilters.value.depots.length > 0) params.depot_ids = activeFilters.value.depots;
    if (activeFilters.value.affiliates.length > 0) params.affiliate_ids = activeFilters.value.affiliates;

    return params;
});

/**
 * Availability is a fact about a moment, and this modal's moment moves while it is
 * open. The query key carries the window so a date change re-asks rather than
 * leaving a stale "free" answer behind - the one failure mode that would be both
 * invisible and wrong.
 */
const queryKey = computed(() => [
    'company-diary-resource-availability',
    props.kind,
    props.startDatetime,
    props.endDatetime,
    props.allDay,
    props.excludeEntryId,
    debouncedSearch.value,
    userStore.tenantId,
    filterParams.value,
]);

const { data, isLoading, isError, refetch } = useQuery<CompanyDiaryAssignableResource[]>({
    queryKey,
    enabled: computed(() => !props.disabled),
    queryFn: async () => {
        const { data: response } = await companyDiaryApi.resourceAvailability(props.kind, {
            ...(props.startDatetime && props.endDatetime ?
                { start_datetime: props.startDatetime, end_datetime: props.endDatetime, all_day: props.allDay }
            :   {}),
            ...(props.excludeEntryId ? { exclude_entry_id: props.excludeEntryId } : {}),
            ...(debouncedSearch.value.trim() ? { search: debouncedSearch.value.trim() } : {}),
            ...filterParams.value,
        });

        return response.data ?? [];
    },
    staleTime: 30_000,
});

const resources = computed<CompanyDiaryAssignableResource[]>(() => data.value ?? []);

const withIssues = computed(() => resources.value.filter((resource) => resource.is_available === false));
const withoutIssues = computed(() => resources.value.filter((resource) => resource.is_available !== false));

const activeSegment = ref<'available' | 'issues'>('available');

const segments = computed(() => [
    { key: 'available' as const, label: 'Available', count: withoutIssues.value.length },
    { key: 'issues' as const, label: 'Issues', count: withIssues.value.length },
]);

const visible = computed(() => (activeSegment.value === 'issues' ? withIssues.value : withoutIssues.value));

/**
 * Labels for the selection chips, resolved from the loaded list first and falling
 * back to what the entry already had. Without the fallback a selected resource
 * loses its name the moment a search - or the Issues segment - filters it out.
 */
const selected = computed(() =>
    selectedIds.value.map((id) => {
        const loaded = resources.value.find((resource) => resource.id === id);

        if (loaded) return loaded;

        const known = (props.assigned ?? []).find((resource) => resource.id === id);

        return {
            id,
            kind: props.kind,
            name: known?.name ?? `#${id}`,
            subtitle: null,
            is_available: null,
            availability_reasons: [],
        } as CompanyDiaryAssignableResource;
    }),
);

const conflictOpen = ref(false);

/**
 * The resource whose conflicts the side panel is showing, held as a whole row
 * rather than an id: the panel outlives a refetch that can reorder or drop the
 * list, and an id would leave it rendering an empty panel under a stale heading.
 */
const conflictResource = ref<CompanyDiaryAssignableResource | null>(null);

const openConflicts = (resource: CompanyDiaryAssignableResource) => {
    conflictResource.value = resource;
    conflictOpen.value = true;
};

const conflictReasons = (resource: CompanyDiaryAssignableResource): CompanyDiaryAvailabilityReason[] =>
    (resource.availability_reasons ?? []).filter((reason) => reason.status !== 'available');

const toggle = (id: number) => {
    if (props.disabled) return;

    emit(
        'update:modelValue',
        isSelected(id) ? selectedIds.value.filter((selectedId) => selectedId !== id) : [...selectedIds.value, id],
    );
};

const initials = (name: string | null): string => {
    const mark = (name ?? '')
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return mark === '' ? '?' : mark;
};
</script>
